import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { 
  Calendar, 
  AlertTriangle, 
  Wrench, 
  DollarSign, 
  Clock, 
  CheckCircle, 
  XCircle,
  Plus,
  Search,
  Download,
  Bell,
  Truck,
  User,
  MapPin,
  Loader2
} from 'lucide-react';
import maintenanceService from '../../services/maintenanceService';

const MaintenanceTracker = ({ vehicleId, companyId, userId, onAddMaintenance, readOnly = false }) => {
  const [activeView, setActiveView] = useState('upcoming'); // upcoming, history, costs
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingRecord, setEditingRecord] = useState(null);
  const [maintenanceData, setMaintenanceData] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [fetchError, setFetchError] = useState(null);
  const [completingIds, setCompletingIds] = useState([]);

  const isContextReady = Boolean(vehicleId && companyId);

  useEffect(() => {
    let isMounted = true;

    if (!isContextReady) {
      setMaintenanceData([]);
      setFetchError(null);
      return () => {
        isMounted = false;
      };
    }

    const loadMaintenanceRecords = async () => {
      setLoadingRecords(true);
      setFetchError(null);

      try {
        const result = await maintenanceService.listMaintenanceRecords({
          vehicleId,
          sortBy: 'scheduledDate',
          sortOrder: 'desc',
          limit: 100
        }, {
          companyId,
          userId
        });

        if (!isMounted) {
          return;
        }

        setMaintenanceData(result.records);
      } catch (error) {
        if (!isMounted) {
          return;
        }

        console.error('Failed to load maintenance records:', error);
        const message = error.getUserFriendlyMessage?.() || error.message || 'Failed to load maintenance records';
        setFetchError(message);
        setMaintenanceData([]);
      } finally {
        if (isMounted) {
          setLoadingRecords(false);
        }
      }
    };

    loadMaintenanceRecords();

    return () => {
      isMounted = false;
    };
  }, [companyId, userId, vehicleId, isContextReady]);

  const allMaintenanceData = maintenanceData;

  const deriveStatus = useCallback((item) => {
    return item?.displayStatus || item?.statusCategory || item?.status || 'unknown';
  }, []);

  // Filter and search logic
  const filteredMaintenance = useMemo(() => {
    const lowerSearch = searchTerm.toLowerCase();

    return allMaintenanceData.filter(item => {
      const statusValue = deriveStatus(item);
      const matchesStatus = filterStatus === 'all' || statusValue === filterStatus || item.status === filterStatus;
      if (!matchesStatus) {
        return false;
      }

      if (!lowerSearch) {
        return true;
      }

      const haystacks = [item.type, item.description, item.vendor, item.technician, item.workOrderNumber]
        .filter(Boolean)
        .map(value => String(value).toLowerCase());

      return haystacks.some(value => value.includes(lowerSearch));
    });
  }, [allMaintenanceData, filterStatus, deriveStatus, searchTerm]);

  // Get upcoming maintenance (next 30 days)
  const upcomingMaintenance = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const thirtyDaysFromNow = new Date(today);
    thirtyDaysFromNow.setDate(today.getDate() + 30);

    return allMaintenanceData
      .filter(item => {
        const statusValue = deriveStatus(item);
        if (['completed', 'cancelled'].includes(statusValue)) {
          return false;
        }

        if (!item.scheduledDate) {
          return false;
        }

        const scheduledDate = new Date(item.scheduledDate);
        return scheduledDate >= today && scheduledDate <= thirtyDaysFromNow;
      })
      .sort((a, b) => new Date(a.scheduledDate) - new Date(b.scheduledDate));
  }, [allMaintenanceData, deriveStatus]);

  // Get overdue maintenance
  const overdueMaintenance = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    return allMaintenanceData.filter(item => {
      const statusValue = deriveStatus(item);
      if (['completed', 'cancelled'].includes(statusValue)) {
        return false;
      }

      if (!item.scheduledDate) {
        return false;
      }

      const scheduledDate = new Date(item.scheduledDate);
      return scheduledDate < today;
    });
  }, [allMaintenanceData, deriveStatus]);

  // Calculate costs
  const totalCosts = useMemo(() => {
    const completed = allMaintenanceData.filter(item => item.status === 'completed');
    const totalSpent = completed.reduce((sum, item) => sum + (item.actualCost || 0), 0);
    const avgCost = completed.length > 0 ? totalSpent / completed.length : 0;
    const estimatedUpcoming = upcomingMaintenance.reduce((sum, item) => sum + (item.estimatedCost || 0), 0);

    return {
      totalSpent,
      avgCost,
      estimatedUpcoming,
      completed: completed.length
    };
  }, [allMaintenanceData, upcomingMaintenance]);

  const getStatusColor = (status) => {
    const value = status || 'unknown';

    switch (value) {
      case 'overdue':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'upcoming':
      case 'pending':
      case 'scheduled':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'in_progress':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'cancelled':
        return 'bg-gray-100 text-gray-700 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical': return 'text-red-600';
      case 'high': return 'text-orange-600';
      case 'medium': return 'text-yellow-600';
      case 'low': return 'text-green-600';
      default: return 'text-gray-600';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) {
      return 'Not set';
    }

    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return 'Not set';
    }

    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getDaysFromNow = (dateString) => {
    if (!dateString) {
      return 'Date not scheduled';
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const targetDate = new Date(dateString);
    if (Number.isNaN(targetDate.getTime())) {
      return 'Date not scheduled';
    }

    const diffTime = targetDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays < 0) {
      const absolute = Math.abs(diffDays);
      return `${absolute} day${absolute === 1 ? '' : 's'} overdue`;
    }
    if (diffDays === 0) return 'Due today';
    if (diffDays === 1) return 'Due tomorrow';
    return `Due in ${diffDays} days`;
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined) {
      return '—';
    }

    const numeric = Number(value);
    if (Number.isNaN(numeric)) {
      return '—';
    }

    return `$${numeric.toLocaleString()}`;
  };

  const formatDateForInput = useCallback((value) => {
    if (!value) {
      return new Date().toISOString().split('T')[0];
    }

    let parsedValue = value;

    if (parsedValue && typeof parsedValue === 'object') {
      if (typeof parsedValue.toDate === 'function') {
        parsedValue = parsedValue.toDate();
      } else if (parsedValue.seconds !== undefined) {
        parsedValue = new Date(parsedValue.seconds * 1000);
      }
    }

    const date = parsedValue instanceof Date ? parsedValue : new Date(parsedValue);
    if (Number.isNaN(date.getTime())) {
      return new Date().toISOString().split('T')[0];
    }

    return date.toISOString().slice(0, 10);
  }, []);

  const sortRecordsByScheduledDateDesc = useCallback((records) => (
    records.slice().sort((a, b) => {
      const aTime = a?.scheduledDate ? new Date(a.scheduledDate).getTime() : 0;
      const bTime = b?.scheduledDate ? new Date(b.scheduledDate).getTime() : 0;
      return bTime - aTime;
    })
  ), []);

  const parseNumberField = useCallback((value) => {
    if (value === null || value === undefined) {
      return undefined;
    }

    const trimmed = String(value).trim();
    if (trimmed === '') {
      return undefined;
    }

    const numeric = Number(trimmed);
    return Number.isNaN(numeric) ? undefined : numeric;
  }, []);

  const buildCreatePayload = useCallback((formValues) => {
    const toIsoDate = (value) => (value ? new Date(`${value}T00:00:00Z`).toISOString() : null);

    const payload = {
      vehicleId,
      type: formValues.type,
      category: formValues.category || 'routine',
      description: formValues.description,
      scheduledDate: toIsoDate(formValues.scheduledDate),
      priority: formValues.priority || 'medium',
      vendor: formValues.vendor || '',
      location: formValues.location || '',
      technician: formValues.technician || '',
      estimatedCost: parseNumberField(formValues.estimatedCost) ?? 0,
      mileage: parseNumberField(formValues.mileage),
      laborHours: parseNumberField(formValues.laborHours),
      parts: formValues.parts
        ? formValues.parts.split(',').map(part => part.trim()).filter(Boolean)
        : [],
      notes: formValues.notes || '',
      status: 'scheduled'
    };

    Object.keys(payload).forEach((key) => {
      if (payload[key] === '' || payload[key] === undefined || payload[key] === null) {
        delete payload[key];
      }
    });

    return payload;
  }, [vehicleId, parseNumberField]);

  const buildUpdatePayload = useCallback((formValues) => {
    const toIsoDate = (value) => (value ? new Date(`${value}T00:00:00Z`).toISOString() : null);

    const payload = {
      type: formValues.type,
      category: formValues.category || 'routine',
      description: formValues.description,
      scheduledDate: toIsoDate(formValues.scheduledDate),
      priority: formValues.priority || 'medium',
      vendor: formValues.vendor || '',
      location: formValues.location || '',
      technician: formValues.technician || '',
      estimatedCost: parseNumberField(formValues.estimatedCost),
      mileage: parseNumberField(formValues.mileage),
      laborHours: parseNumberField(formValues.laborHours),
      parts: formValues.parts
        ? formValues.parts.split(',').map(part => part.trim()).filter(Boolean)
        : [],
      notes: formValues.notes || ''
    };

    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined || payload[key] === null) {
        delete payload[key];
      }
    });

    return payload;
  }, [parseNumberField]);

  const handleCreateMaintenance = useCallback(async (formValues) => {
    if (!companyId) {
      throw new Error('Company context is required to create maintenance records.');
    }

    if (!vehicleId) {
      throw new Error('Vehicle context is required to create maintenance records.');
    }

    const payload = buildCreatePayload(formValues);

    try {
      const { record } = await maintenanceService.createMaintenanceRecord(payload, {
        companyId,
        userId
      });

      setMaintenanceData((prev) => {
        const next = [record, ...prev.filter(item => item.id !== record.id)];
        return sortRecordsByScheduledDateDesc(next);
      });

      if (onAddMaintenance) {
        onAddMaintenance(record);
      }

      return record;
    } catch (error) {
      console.error('Failed to create maintenance record:', error);
      throw error;
    }
  }, [buildCreatePayload, companyId, vehicleId, userId, onAddMaintenance, sortRecordsByScheduledDateDesc]);

  const handleUpdateMaintenance = useCallback(async (formValues, recordId, baseRecord) => {
    if (!recordId) {
      throw new Error('Maintenance record identifier is required for updates.');
    }

    if (!companyId) {
      throw new Error('Company context is required to update maintenance records.');
    }

    const payload = buildUpdatePayload(formValues);

    if (baseRecord?.vehicleId && payload.vehicleId === undefined) {
      payload.vehicleId = baseRecord.vehicleId;
    }

    try {
      const { record } = await maintenanceService.updateMaintenanceRecord(recordId, payload, {
        companyId,
        userId
      });

      setMaintenanceData((prev) => {
        const next = prev.map(item => (item.id === record.id ? record : item));
        return sortRecordsByScheduledDateDesc(next);
      });

      return record;
    } catch (error) {
      console.error('Failed to update maintenance record:', error);
      throw error;
    }
  }, [buildUpdatePayload, companyId, userId, sortRecordsByScheduledDateDesc]);

  const handleModalSubmit = useCallback((formValues, recordId) => {
    if (modalMode === 'edit') {
      return handleUpdateMaintenance(formValues, recordId, editingRecord);
    }
    return handleCreateMaintenance(formValues);
  }, [modalMode, handleUpdateMaintenance, handleCreateMaintenance, editingRecord]);

  const handleMarkComplete = useCallback(async (maintenance) => {
    if (readOnly) {
      return;
    }

    if (!maintenance?.id) {
      return;
    }

    if (!companyId) {
      setFetchError('Company context is required to complete maintenance records.');
      return;
    }

    const recordId = maintenance.id;
    setFetchError(null);
    setCompletingIds(prev => (prev.includes(recordId) ? prev : [...prev, recordId]));

    const resolvedActualCost = parseNumberField(
      maintenance.actualCost ?? maintenance.cost ?? maintenance.estimatedCost ?? 0
    ) ?? 0;

    const payload = {
      actualCost: resolvedActualCost,
      mileage: parseNumberField(maintenance.mileage),
      technician: maintenance.technician || undefined,
      vendor: maintenance.vendor || undefined,
      notes: maintenance.notes || undefined,
      parts: Array.isArray(maintenance.parts) && maintenance.parts.length ? maintenance.parts : undefined
    };

    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    try {
      const { record } = await maintenanceService.completeMaintenanceRecord(recordId, payload, {
        companyId,
        userId
      });

      setMaintenanceData(prev => {
        const next = prev.map(item => (item.id === record.id ? record : item));
        return sortRecordsByScheduledDateDesc(next);
      });
    } catch (error) {
      console.error('Failed to complete maintenance record:', error);
      const message = error.getUserFriendlyMessage?.() || error.message || 'Failed to complete maintenance record. Please try again.';
      setFetchError(message);
    } finally {
      setCompletingIds(prev => prev.filter(id => id !== recordId));
    }
  }, [companyId, userId, parseNumberField, sortRecordsByScheduledDateDesc, readOnly]);

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setEditingRecord(null);
  }, []);

  const openCreateModal = useCallback(() => {
    if (readOnly) return;
    setModalMode('create');
    setEditingRecord(null);
    setFetchError(null);
    setIsModalOpen(true);
  }, [readOnly]);

  const openEditModal = useCallback((record) => {
    if (readOnly) return;
    setModalMode('edit');
    setEditingRecord(record);
    setFetchError(null);
    setIsModalOpen(true);
  }, [readOnly]);

  const MaintenanceModal = ({ mode, initialData, onClose, onSubmit }) => {
    const createInitialFormState = () => {
      const scheduledDate = initialData?.scheduledDate || null;
      const locationValue = initialData?.location;
      const partsString = Array.isArray(initialData?.parts)
        ? initialData.parts
            .map(part => (typeof part === 'string' ? part : part?.name || ''))
            .filter(Boolean)
            .join(', ')
        : '';

      return {
        type: initialData?.type || '',
        category: initialData?.category || 'routine',
        description: initialData?.description || '',
        scheduledDate: formatDateForInput(scheduledDate),
        priority: initialData?.priority || 'medium',
        vendor: initialData?.vendor || '',
        location: locationValue !== undefined && locationValue !== null
          ? locationValue
          : mode === 'edit'
            ? ''
            : 'Main Shop',
        technician: initialData?.technician || '',
        estimatedCost: (() => {
          const value = initialData?.estimatedCost ?? initialData?.actualCost ?? initialData?.cost;
          if (value === undefined || value === null) {
            return '';
          }
          return String(value);
        })(),
        mileage: initialData?.mileage !== undefined && initialData?.mileage !== null
          ? String(initialData.mileage)
          : '',
        parts: partsString,
        laborHours: initialData?.laborHours !== undefined && initialData?.laborHours !== null
          ? String(initialData.laborHours)
          : '',
        notes: initialData?.notes || ''
      };
    };

    const [formData, setFormData] = useState(createInitialFormState);
    const [formError, setFormError] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
      setFormData(createInitialFormState());
      setFormError(null);
    }, [initialData, mode]);

    const maintenanceTypes = [
      'Oil Change',
      'Tire Rotation',
      'Brake Service',
      'Engine Diagnostic',
      'Transmission Service',
      'DOT Inspection',
      'Preventive Maintenance',
      'Repair',
      'Other'
    ];

    const handleSubmit = async (e) => {
      e.preventDefault();
      setFormError(null);

      if (!onSubmit) {
        onClose();
        return;
      }

      setSubmitting(true);

      try {
        await onSubmit(formData, initialData?.id);
        onClose();
      } catch (error) {
        console.error('Failed to save maintenance record:', error);
        const message = error.getUserFriendlyMessage?.() || error.message || 'Failed to save maintenance record. Please try again.';
        setFormError(message);
      } finally {
        setSubmitting(false);
      }
    };

    const handleChange = (field, value) => {
      setFormData(prev => ({ ...prev, [field]: value }));
    };

    const modalTitle = mode === 'edit' ? 'Edit Maintenance' : 'Add New Maintenance';
    const submitLabel = submitting
      ? (
        <span className="flex items-center">
          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          Saving...
        </span>
      )
      : mode === 'edit'
        ? 'Save Changes'
        : 'Add Maintenance';

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
        <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-hidden">
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-semibold text-gray-900">{modalTitle}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
            >
              <XCircle className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Maintenance Type */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Maintenance Type *
                </label>
                <select
                  value={formData.type}
                  onChange={(e) => handleChange('type', e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select Type</option>
                  {maintenanceTypes.map(type => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Category */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Category
                </label>
                <select
                  value={formData.category}
                  onChange={(e) => handleChange('category', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="routine">Routine</option>
                  <option value="repair">Repair</option>
                  <option value="inspection">Inspection</option>
                </select>
              </div>

              {/* Scheduled Date */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scheduled Date *
                </label>
                <input
                  type="date"
                  value={formData.scheduledDate}
                  onChange={(e) => handleChange('scheduledDate', e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Priority */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Priority
                </label>
                <select
                  value={formData.priority}
                  onChange={(e) => handleChange('priority', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
              </div>

              {/* Vendor */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vendor
                </label>
                <input
                  type="text"
                  value={formData.vendor}
                  onChange={(e) => handleChange('vendor', e.target.value)}
                  placeholder="e.g., Quick Lube Service"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Technician */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Technician
                </label>
                <input
                  type="text"
                  value={formData.technician}
                  onChange={(e) => handleChange('technician', e.target.value)}
                  placeholder="e.g., Mike Johnson"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Estimated Cost */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estimated Cost
                </label>
                <input
                  type="number"
                  value={formData.estimatedCost}
                  onChange={(e) => handleChange('estimatedCost', e.target.value)}
                  placeholder="0.00"
                  step="0.01"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Mileage */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Current Mileage
                </label>
                <input
                  type="number"
                  value={formData.mileage}
                  onChange={(e) => handleChange('mileage', e.target.value)}
                  placeholder="e.g., 125000"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Labor Hours */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estimated Labor Hours
                </label>
                <input
                  type="number"
                  value={formData.laborHours}
                  onChange={(e) => handleChange('laborHours', e.target.value)}
                  placeholder="e.g., 2.5"
                  step="0.1"
                  min="0"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>

              {/* Location */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Location
                </label>
                <input
                  type="text"
                  value={formData.location}
                  onChange={(e) => handleChange('location', e.target.value)}
                  placeholder="e.g., Main Shop"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Description */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description *
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => handleChange('description', e.target.value)}
                required
                rows={3}
                placeholder="Describe the maintenance work to be performed..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Parts */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Parts Needed
              </label>
              <input
                type="text"
                value={formData.parts}
                onChange={(e) => handleChange('parts', e.target.value)}
                placeholder="e.g., Oil Filter, 5W-30 Motor Oil (comma separated)"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {/* Notes */}
            <div className="mt-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                rows={3}
                placeholder="Additional notes or special instructions..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            {formError && (
              <div className="mt-6 bg-red-50 border border-red-200 text-red-700 text-sm rounded-md px-3 py-2">
                {formError}
              </div>
            )}

            {/* Form Actions */}
            <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-60 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {submitLabel}
              </button>
            </div>
          </form>
        </div>
      </div>
    );
  };

  const MaintenanceCard = ({ maintenance }) => {
    const statusValue = deriveStatus(maintenance);
    const statusLabel = statusValue.replace(/_/g, ' ');
    const vendor = maintenance.vendor || '—';
    const technician = maintenance.technician || '—';
    const mileageLabel = maintenance.mileage !== null && maintenance.mileage !== undefined
      ? `${Number(maintenance.mileage).toLocaleString()} miles`
      : 'Mileage not recorded';

    const costLabel = maintenance.status === 'completed'
      ? formatCurrency(maintenance.actualCost || maintenance.cost)
      : `Est. ${formatCurrency(maintenance.estimatedCost)}`;

    const isCompleting = completingIds.includes(maintenance.id);

    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md transition-shadow">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <h3 className="font-semibold text-gray-900">{maintenance.type}</h3>
              <span className={`px-2 py-1 text-xs font-medium rounded-full border ${getStatusColor(statusValue)}`}>
                {statusLabel.charAt(0).toUpperCase() + statusLabel.slice(1)}
              </span>
            </div>
            <p className="text-sm text-gray-600">{maintenance.description || 'No description provided.'}</p>
          </div>
          <div className={`${getPriorityColor(maintenance.priority)}`}>
            <AlertTriangle className="h-4 w-4" />
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <div className="flex items-center text-gray-600">
            <Calendar className="h-4 w-4 mr-2" />
            <span>{formatDate(maintenance.scheduledDate)}</span>
            <span className="ml-2 text-xs">({getDaysFromNow(maintenance.scheduledDate)})</span>
          </div>

          <div className="flex items-center text-gray-600">
            <DollarSign className="h-4 w-4 mr-2" />
            <span>{costLabel}</span>
          </div>

          <div className="flex items-center text-gray-600">
            <MapPin className="h-4 w-4 mr-2" />
            <span>{vendor}</span>
          </div>

          <div className="flex items-center text-gray-600">
            <User className="h-4 w-4 mr-2" />
            <span>{technician}</span>
          </div>
        </div>

        {maintenance.notes && (
          <div className="mt-3 p-2 bg-gray-50 rounded text-sm text-gray-700">
            <strong>Notes:</strong> {maintenance.notes}
          </div>
        )}

        <div className="flex justify-between items-center mt-4">
          <div className="text-xs text-gray-500">
            {mileageLabel}
          </div>
          {!readOnly && (
            <div className="flex space-x-2">
              <button
                onClick={() => openEditModal(maintenance)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                Edit
              </button>
              {!['completed', 'cancelled'].includes(statusValue) && (
                <button
                  onClick={() => handleMarkComplete(maintenance)}
                  disabled={isCompleting}
                  className={`text-sm font-medium flex items-center ${
                    isCompleting
                      ? 'text-green-400 cursor-wait'
                      : 'text-green-600 hover:text-green-800'
                  }`}
                >
                  {isCompleting ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                      Completing...
                    </>
                  ) : (
                    'Mark Complete'
                  )}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
    );
  };

  const UpcomingView = () => (
    <div className="space-y-6">
      {/* Alerts */}
      {overdueMaintenance.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <AlertTriangle className="h-5 w-5 text-red-600 mr-2" />
            <h3 className="text-sm font-semibold text-red-800">
              {overdueMaintenance.length} Overdue Maintenance Item{overdueMaintenance.length !== 1 ? 's' : ''}
            </h3>
          </div>
          <p className="text-sm text-red-700 mt-1">
            These items require immediate attention to maintain vehicle safety and compliance.
          </p>
        </div>
      )}
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-red-100 rounded-lg">
              <AlertTriangle className="h-5 w-5 text-red-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Overdue</p>
              <p className="text-2xl font-semibold text-gray-900">{overdueMaintenance.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded-lg">
              <Clock className="h-5 w-5 text-yellow-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Upcoming</p>
              <p className="text-2xl font-semibold text-gray-900">{upcomingMaintenance.length}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Wrench className="h-5 w-5 text-blue-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">In Progress</p>
              <p className="text-2xl font-semibold text-gray-900">
                {allMaintenanceData.filter(item => deriveStatus(item) === 'in_progress').length}
              </p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <CheckCircle className="h-5 w-5 text-green-600" />
            </div>
            <div className="ml-3">
              <p className="text-sm font-medium text-gray-600">Completed</p>
              <p className="text-2xl font-semibold text-gray-900">{totalCosts.completed}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Upcoming Maintenance List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Next 30 Days</h3>
        </div>
        <div className="p-4 space-y-4">
          {upcomingMaintenance.length > 0 ? (
            upcomingMaintenance.map(maintenance => (
              <MaintenanceCard key={maintenance.id} maintenance={maintenance} />
            ))
          ) : (
            <div className="text-center py-8 text-gray-500">
              <Wrench className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No maintenance scheduled for the next 30 days</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const HistoryView = () => (
    <div className="space-y-4">
      {filteredMaintenance.map(maintenance => (
        <MaintenanceCard key={maintenance.id} maintenance={maintenance} />
      ))}
      {filteredMaintenance.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <Search className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No maintenance records match your filters</p>
        </div>
      )}
    </div>
  );

  const CostView = () => (
    <div className="space-y-6">
      {/* Cost Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-green-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total Spent</p>
              <p className="text-2xl font-semibold text-gray-900">{formatCurrency(totalCosts.totalSpent)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-blue-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Average Cost</p>
              <p className="text-2xl font-semibold text-gray-900">{formatCurrency(totalCosts.avgCost)}</p>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <div className="p-3 bg-yellow-100 rounded-lg">
              <DollarSign className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Estimated Upcoming</p>
              <p className="text-2xl font-semibold text-gray-900">{formatCurrency(totalCosts.estimatedUpcoming)}</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* Cost Breakdown */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Cost Breakdown by Category</h3>
        </div>
        <div className="p-6">
          {['routine', 'repair', 'inspection'].map(category => {
            const categoryItems = allMaintenanceData.filter(item => 
              item.category === category && item.status === 'completed'
            );
            const categoryTotal = categoryItems.reduce((sum, item) => sum + (item.actualCost || item.cost || 0), 0);
            const percentage = totalCosts.totalSpent > 0 ? (categoryTotal / totalCosts.totalSpent) * 100 : 0;
            
            return (
              <div key={category} className="mb-4">
                <div className="flex justify-between items-center mb-2">
                  <span className="text-sm font-medium text-gray-900 capitalize">{category}</span>
                  <span className="text-sm text-gray-600">{formatCurrency(categoryTotal)} ({percentage.toFixed(1)}%)</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div 
                    className={`h-2 rounded-full ${
                      category === 'routine' ? 'bg-green-500' :
                      category === 'repair' ? 'bg-red-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Wrench className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <h1 className="text-xl font-semibold text-gray-900">Maintenance Tracker</h1>
              <p className="text-sm text-gray-600">Vehicle {vehicleId || 'T-001'}</p>
            </div>
          </div>
          
          {!readOnly && (
            <div className="flex items-center space-x-3">
              <button
                type="button"
                onClick={openCreateModal}
                disabled={!isContextReady}
                className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg transition-colors ${
                  !isContextReady ? 'opacity-60 cursor-not-allowed' : 'hover:bg-blue-700'
                }`}
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Maintenance
              </button>
              <button
                type="button"
                disabled={!maintenanceData.length}
                className={`flex items-center px-4 py-2 text-gray-600 border border-gray-300 rounded-lg transition-colors ${
                  maintenanceData.length ? 'hover:bg-gray-50' : 'opacity-60 cursor-not-allowed'
                }`}
              >
                <Download className="h-4 w-4 mr-2" />
                Export
              </button>
            </div>
          )}
        </div>
      </div>

      {!isContextReady && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm rounded-lg px-4 py-3">
          Select a company and vehicle to load maintenance records.
        </div>
      )}

      {fetchError && isContextReady && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3">
          {fetchError}
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <nav className="flex overflow-x-auto">
          {[
            { id: 'upcoming', name: 'Upcoming', icon: Bell },
            { id: 'history', name: 'History', icon: Clock },
            { id: 'costs', name: 'Costs', icon: DollarSign }
          ].map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveView(tab.id)}
                className={`flex items-center space-x-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                  activeView === tab.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Icon className="h-4 w-4" />
                <span>{tab.name}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Filters */}
      {activeView === 'history' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-col sm:flex-row space-y-3 sm:space-y-0 sm:space-x-4">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search maintenance records..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  disabled={!isContextReady || loadingRecords || !maintenanceData.length}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
                />
              </div>
            </div>
            <div className="sm:w-48">
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                disabled={!isContextReady || loadingRecords || !maintenanceData.length}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed"
              >
                <option value="all">All Status</option>
                <option value="overdue">Overdue</option>
                <option value="upcoming">Upcoming</option>
                <option value="pending">Pending</option>
                <option value="scheduled">Scheduled</option>
                <option value="in_progress">In Progress</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="min-h-96">
        {loadingRecords ? (
          <div className="flex flex-col items-center justify-center h-48 text-gray-500">
            <Loader2 className="h-6 w-6 mb-2 animate-spin" />
            <p>Loading maintenance records...</p>
          </div>
        ) : !isContextReady ? (
          <div className="flex flex-col items-center justify-center h-48 text-gray-500">
            <Truck className="h-10 w-10 mb-2 opacity-40" />
            <p>Select a vehicle to view maintenance details.</p>
          </div>
        ) : (
          <>
            {activeView === 'upcoming' && <UpcomingView />}
            {activeView === 'history' && <HistoryView />}
            {activeView === 'costs' && <CostView />}
          </>
        )}
      </div>

      {/* Maintenance Modal */}
      {isModalOpen && (
        <MaintenanceModal
          mode={modalMode}
          initialData={editingRecord}
          onClose={handleModalClose}
          onSubmit={handleModalSubmit}
        />
      )}
    </div>
  );
};

export default MaintenanceTracker;
